import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Check, Contact, KeyRound, Languages, Loader2, Mail, MousePointerClick, Network, Pencil, Plus, Send, UserRound, X } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ProfilePhotoDialog } from "./ProfilePhotoDialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { ResizeHandles } from "./ui/resize-handles";
import { useResizable } from "../hooks/useResizable";
import { CollaboratorLanguageFields } from "./CollaboratorLanguageFields";
import { spokenLanguageNativeName } from "../lib/spoken-languages";

/**
 * Fiche collaborateur — popup redimensionnable (18/09/2026).
 *
 * Remplace le formulaire qui se dépliait DANS la liste (bureau) et son doublon mobile, sans
 * créer un nouveau formulaire : ce sont les mêmes contrôles, déplacés ici, qui prennent
 * trois états :
 *  - LECTURE : valeurs affichées, statut, actions, simulateurs directement gérables ;
 *  - ÉDITION GLOBALE (bouton « Modifier la fiche ») : tous les champs, Enregistrer / Annuler ;
 *  - ÉDITION D'UN CHAMP (double-clic, ou Entrée / petit crayon pour clavier et tactile) :
 *    ce seul champ s'ouvre, avec ✓ / ✕.
 *
 * L'état de la saisie vit chez l'appelant (`AccountPage`, liste `collaborators`), comme avant :
 * `onChange` écrit dans la liste, les annulations restaurent un instantané. Le composant ne
 * parle jamais à l'API — l'appelant enregistre (`onSave`, `onSaveField`).
 *
 * Rendue EN DEHORS du tableau des collaborateurs : ses clics remonteraient sinon jusqu'à la
 * ligne (React fait remonter les événements le long de l'arbre des composants, portails compris).
 */

export interface FicheCollaborator {
  id: string;
  civility: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profileType: string;
  status: string;
  position: string;
  linkedinUrl?: string;
  profilePicture?: string;
  licences: string[];
  managerId?: string | null;
  language?: string;
  spokenLanguage?: string | null;
}

/** Champs éditables un par un. `languages` = les deux langues, portées par un même composant. */
export type FicheFieldKey =
  | "civility" | "firstName" | "lastName" | "email" | "phoneNumber" | "position" | "linkedinUrl"
  | "languages" | "profileType" | "managerId";

type EditableProp = Exclude<keyof FicheCollaborator, "id" | "licences" | "status">;

/** Les propriétés qu'un champ de la fiche couvre (instantané à restaurer si on annule). */
const FIELD_PROPS: Record<FicheFieldKey, EditableProp[]> = {
  civility: ["civility"],
  firstName: ["firstName"],
  lastName: ["lastName"],
  email: ["email"],
  phoneNumber: ["phoneNumber"],
  position: ["position"],
  linkedinUrl: ["linkedinUrl"],
  languages: ["language", "spokenLanguage"],
  profileType: ["profileType"],
  managerId: ["managerId"],
};

export interface FicheLicenceOption {
  type: string;
  label: string;
  color: string;
  used: number;
  total: number;
  isOneShot?: boolean;
}

export interface FicheRights {
  edit: boolean;
  manager: boolean;
  profile: boolean;
  licences: boolean;
  invite: boolean;
}

interface CollaboratorFicheDialogProps {
  collab: FicheCollaborator;
  /** Responsables possibles (ORG/MNG du périmètre visible, hors le collaborateur lui-même). */
  managerCandidates: { id: string; firstName: string; lastName: string }[];
  /** Licences du compte (catalogue + consommation) — sert aux pastilles ET au menu d'ajout. */
  licencePool: FicheLicenceOption[];
  rights: FicheRights;
  /** Profils que l'utilisateur connecté peut attribuer (`assignableProfiles`). */
  profileOptions: readonly string[];
  /** Badge de statut (et de profil pour l'ADM) — le même que dans la liste. */
  statusBadge: React.ReactNode;
  /** Actions secondaires (`CollaboratorRowActions variant="toolbar"`). */
  actions?: React.ReactNode;
  /** Mode édition globale — piloté par l'appelant (`editingUserId`). */
  editing: boolean;
  /** Des modifications de l'édition globale ne sont pas enregistrées. */
  dirty: boolean;
  saving: boolean;
  resendingLink: boolean;
  onClose: () => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancelEdit: () => void;
  onChange: (prop: EditableProp, value: string | null) => void;
  /** Enregistre UN champ. Renvoie un message d'erreur, ou `null` si c'est enregistré. */
  onSaveField: (field: FicheFieldKey, snapshot: Partial<FicheCollaborator>) => Promise<string | null>;
  onToggleLicence: (type: string) => void;
  /**
   * Licence d'abonnement Starter / Focus : simulateurs choisis pour cette personne
   * (`ids` / `names` à `null` = pas encore choisis ; `names` à `null` aussi tant que la liste charge).
   * `null` pour toute autre licence.
   */
  licenceSimulators?: (type: string) => { max: number; ids: string[] | null; names: string[] | null } | null;
  /** Ouvre la fenêtre de choix des simulateurs de cette licence. */
  onEditLicenceSimulators?: (type: string) => void;
  onInvite: () => void;
  onResendLink: () => void;
  /** Enregistre la photo choisie dans la fenêtre partagée avec « Mon profil ». Erreur ou `null`. */
  onSavePhoto: (photo: string) => Promise<string | null>;
}

const PROFILE_LABEL_KEYS: Record<string, string> = {
  ORG: "account.organisateur",
  MNG: "account.manager",
  RH: "account.rhManagement",
  GES: "account.profileGes",
  BNF: "account.user",
};

/** Accent de chaque section (fond pastel de la pastille + texte contrasté de la charte). */
const SECTION_TONES = {
  blue: "bg-[#A6D3F9]/40 text-[#384872]",
  green: "bg-[#7CBF94]/25 text-[#1E7A3C]",
  violet: "bg-[#988EF2]/25 text-[#5A4BBE]",
  yellow: "bg-[#F6C847]/30 text-[#926B00]",
  coral: "bg-[#F49390]/25 text-[#C0423F]",
} as const;

export function CollaboratorFicheDialog({
  collab,
  managerCandidates,
  licencePool,
  rights,
  profileOptions,
  statusBadge,
  actions,
  editing,
  dirty,
  saving,
  resendingLink,
  onClose,
  onStartEdit,
  onSave,
  onCancelEdit,
  onChange,
  onSaveField,
  onToggleLicence,
  licenceSimulators,
  onEditLicenceSimulators,
  onInvite,
  onResendLink,
  onSavePhoto,
}: CollaboratorFicheDialogProps) {
  const { t } = useTranslation();
  const [photoOpen, setPhotoOpen] = useState(false);
  const { containerRef, onPointerDown, boxStyle } = useResizable({
    minWidth: 360,
    minHeight: 360,
    maxWidth: typeof window !== "undefined" ? window.innerWidth - 32 : 1200,
    maxHeight: typeof window !== "undefined" ? window.innerHeight - 32 : 900,
  });

  // Édition d'UN champ : sa clé + l'instantané des propriétés qu'il couvre.
  const [activeField, setActiveField] = useState<{ key: FicheFieldKey; snapshot: Partial<FicheCollaborator> } | null>(null);
  const [fieldBusy, setFieldBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Le clic sur le fond ne ferme que s'il a COMMENCÉ sur le fond. Sans cela, relâcher la
  // souris hors de la fenêtre à la fin d'un redimensionnement produisait un « clic » sur le
  // fond (ancêtre commun du appui et du relâchement) : la fiche se fermait en l'agrandissant.
  const pressStartedOnBackdrop = useRef(false);

  // Changer de collaborateur (ou passer en édition globale) referme l'édition d'un champ.
  useEffect(() => { setActiveField(null); setFieldError(null); }, [collab.id]);
  useEffect(() => { if (editing) { setActiveField(null); setFieldError(null); } }, [editing]);

  useEffect(() => { containerRef.current?.focus(); }, [containerRef]);

  const canEditField = (key: FicheFieldKey): boolean => {
    if (key === "profileType") return rights.profile;
    if (key === "managerId") return rights.manager;
    return rights.edit;
  };

  const fieldIsDirty = (field: { key: FicheFieldKey; snapshot: Partial<FicheCollaborator> }) =>
    FIELD_PROPS[field.key].some((p) => (field.snapshot[p] ?? null) !== ((collab as any)[p] ?? null));

  const restoreField = (field: { key: FicheFieldKey; snapshot: Partial<FicheCollaborator> }) => {
    for (const p of FIELD_PROPS[field.key]) onChange(p, (field.snapshot[p] as string | null | undefined) ?? null);
  };

  const startField = (key: FicheFieldKey) => {
    if (editing || !canEditField(key)) return;
    if (activeField) {
      if (activeField.key === key) return;
      // Un champ modifié garde la main tant qu'il n'est ni enregistré ni annulé.
      if (fieldIsDirty(activeField)) return;
    }
    const snapshot: Partial<FicheCollaborator> = {};
    for (const p of FIELD_PROPS[key]) (snapshot as any)[p] = (collab as any)[p] ?? null;
    setFieldError(null);
    setActiveField({ key, snapshot });
  };

  const cancelField = () => {
    if (!activeField) return;
    restoreField(activeField);
    setActiveField(null);
    setFieldError(null);
  };

  const saveField = async () => {
    if (!activeField || fieldBusy) return;
    if (!fieldIsDirty(activeField)) { setActiveField(null); return; }
    setFieldBusy(true);
    setFieldError(null);
    try {
      const error = await onSaveField(activeField.key, activeField.snapshot);
      if (error) setFieldError(error);
      else setActiveField(null);
    } finally {
      setFieldBusy(false);
    }
  };

  const requestClose = () => {
    if (activeField) restoreField(activeField);
    if (editing && dirty) { setConfirmDiscard(true); return; }
    if (editing) onCancelEdit();
    onClose();
  };

  // Échap ferme la fiche — sauf quand il vient d'un calque ouvert AU-DESSUS (liste
  // déroulante, menu, dialogue) : ceux-là sont rendus hors de la fiche dans le DOM, et
  // Radix les referme lui-même. Sans ce filtre, un Échap fermait deux calques d'un coup.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    if (!containerRef.current?.contains(e.target as Node)) return;
    e.preventDefault();
    requestClose();
  };

  const modeOf = (key: FicheFieldKey): FieldMode =>
    editing ? "global" : activeField?.key === key ? "single" : "read";

  const fieldProps = (key: FicheFieldKey) => ({
    fieldKey: key,
    mode: modeOf(key),
    canEdit: canEditField(key),
    busy: fieldBusy,
    error: activeField?.key === key ? fieldError : null,
    onStart: () => startField(key),
    onSave: saveField,
    onCancel: cancelField,
  });

  const fullName = `${collab.firstName} ${collab.lastName}`.trim();
  const initials = `${(collab.firstName[0] || "").toUpperCase()}${(collab.lastName[0] || "").toUpperCase()}`;
  const manager = managerCandidates.find((m) => m.id === collab.managerId);
  const civilityLabel = (value?: string | null) =>
    value === "Mme" ? t("account.fiche.civilityMrs") : value === "M." ? t("account.fiche.civilityMr") : value || "";
  const profileLabel = (p: string) =>
    PROFILE_LABEL_KEYS[p] ? t(PROFILE_LABEL_KEYS[p]!) : t(`admin.profileTypes.${p}`, { defaultValue: p });
  const languageLabel = (code?: string | null) => (code === "en" ? t("language.en") : t("language.fr"));
  const spokenLabel = collab.spokenLanguage ? spokenLanguageNativeName(collab.spokenLanguage) : t("account.spokenLanguageSame");
  // Les packs one-shot sont mutualisés au niveau COMPTE (account_licence_grants) : ne jamais
  // les proposer à l'assignation PAR-USER (assignation inopérante).
  const addableLicences = licencePool.filter((lp) => !collab.licences.includes(lp.type) && !lp.isOneShot);
  // Liste des profils : ceux qu'on peut attribuer, plus l'actuel (pour qu'il reste lisible).
  const profileChoices = ["ORG", "MNG", "RH", "GES", "BNF"].filter(
    (p) => profileOptions.includes(p) || p === collab.profileType,
  );
  const canEditSomething = rights.edit || rights.manager || rights.profile;
  const linkedinHref = collab.linkedinUrl
    ? (/^https?:\/\//i.test(collab.linkedinUrl) ? collab.linkedinUrl : `https://${collab.linkedinUrl}`)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1B2440]/50 backdrop-blur-[2px] p-2"
      onPointerDown={(e) => { pressStartedOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        const fromBackdrop = pressStartedOnBackdrop.current && e.target === e.currentTarget;
        pressStartedOnBackdrop.current = false;
        if (fromBackdrop && !document.body.style.cursor) requestClose();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="collaborator-fiche-title"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="relative bg-[#F4F6FB] text-card-foreground rounded-2xl shadow-[0_24px_60px_-12px_rgba(27,36,64,0.45)] ring-1 ring-black/5 flex flex-col overflow-hidden outline-none"
        style={boxStyle(820, 760)}
        data-testid="collaborator-fiche"
      >
        {/* En-tête : identité, statut, fermeture — en relief sur un dégradé de la charte */}
        <div className="relative flex items-start gap-4 px-6 pt-6 pb-5 bg-gradient-to-br from-[#A6D3F9]/55 via-white to-[#988EF2]/25 border-b border-[#384872]/10">
          {/* Photo de profil — la même fenêtre que « Mon profil » (ProfilePhotoDialog). */}
          <div className="relative shrink-0">
            <Avatar className="h-16 w-16 rounded-2xl shadow-md ring-2 ring-white">
              <AvatarImage src={collab.profilePicture || undefined} className="object-cover" />
              <AvatarFallback className="rounded-2xl bg-white text-[#384872] text-lg font-bold font-['Sora']">{initials}</AvatarFallback>
            </Avatar>
            {rights.edit && (
              <button
                type="button"
                onClick={() => setPhotoOpen(true)}
                className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-[#384872] text-white flex items-center justify-center shadow-md ring-2 ring-white hover:bg-[#2c3a5e] transition-colors"
                aria-label={t("account.changePhoto")}
                title={t("account.changePhoto")}
                data-testid="fiche-photo"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="collaborator-fiche-title" className="font-['Sora'] font-bold text-xl text-[#384872] truncate">
              {fullName || t("account.fiche.title")}
            </h2>
            <p className="text-sm text-[#384872]/75 truncate">
              {[collab.position, collab.email].filter(Boolean).join(" · ")}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">{statusBadge}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-full bg-white/70 hover:bg-white shadow-sm" aria-label={t("common.close")} onClick={requestClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Barre d'actions — de vrais boutons, libellés, sur un bandeau distinct */}
        {/* Sur téléphone : UNE ligne qui défile, plutôt que quatre lignes empilées.
            Mesuré à 390 px avant correction : 193 px de hauteur pour cinq boutons répartis
            sur QUATRE lignes — l'en-tête mangeait un quart de l'écran avant le premier
            champ de la fiche. Le `flex-1` ci-dessous y contribuait : sur un conteneur qui
            passe à la ligne, il absorbe la place restante et renvoie tout ce qui suit sur
            une ligne de plus.
            `[&>*]:shrink-0` verrouille la largeur des boutons : sans lui, une ligne qui
            défile les COMPRIME au lieu de déborder, et les libellés se tronquent.
            Au-delà de 640 px, rien ne change : on retrouve le retour à la ligne d'origine. */}
        <div className="flex flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-x-visible [&>*]:shrink-0 sm:[&>*]:shrink items-center gap-2 px-6 py-3 bg-white border-b border-slate-200 shadow-[0_2px_6px_-2px_rgba(27,36,64,0.12)] relative z-[1]">
          {rights.invite && collab.status === "created" && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5 border-2 border-[#A6D3F9] bg-white text-[#384872] shadow-sm font-medium hover:bg-[#A6D3F9]/20 hover:shadow" onClick={onInvite} disabled={resendingLink}>
              <Send className={`h-4 w-4 ${resendingLink ? "animate-pulse" : ""}`} />
              {t("account.invite")}
            </Button>
          )}
          {rights.invite && (collab.status === "invited" || collab.status === "active") && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5 border-2 border-[#A6D3F9] bg-white text-[#384872] shadow-sm font-medium hover:bg-[#A6D3F9]/20 hover:shadow" onClick={onResendLink} disabled={resendingLink}>
              <Mail className={`h-4 w-4 ${resendingLink ? "animate-pulse" : ""}`} />
              {t("account.resendLink")}
            </Button>
          )}
          {actions}
          {/* Espaceur : il pousse l'action principale à droite. Sur une seule ligne qui
              défile, il n'a plus de sens et ajouterait un vide à faire glisser. */}
          <div className="hidden sm:block sm:flex-1" />
          {canEditSomething && !editing && (
            <Button size="sm" className="h-9 gap-1.5 bg-[#384872] hover:bg-[#2c3a5e] text-white shadow-md font-medium" onClick={onStartEdit} data-testid="fiche-edit">
              <Pencil className="h-4 w-4" />
              {t("account.fiche.editAll")}
            </Button>
          )}
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          {!editing && canEditSomething && (
            <p className="flex items-center gap-1.5 text-xs text-[#384872]/70">
              <MousePointerClick className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden md:inline">{t("account.fiche.doubleClickHint")}</span>
              <span className="md:hidden">{t("account.fiche.touchHint")}</span>
            </p>
          )}

          <FicheSection title={t("account.fiche.identity")} icon={<UserRound className="h-4 w-4" />} tone="blue">
            {/* Civilité en colonne étroite : quatre caractères ne méritent pas un tiers de ligne. */}
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)]">
              <FicheField label={t("account.civility")} readValue={civilityLabel(collab.civility)} {...fieldProps("civility")}>
                <Select value={collab.civility} onValueChange={(value) => onChange("civility", value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("common.confirm")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M.">{civilityLabel("M.")}</SelectItem>
                    <SelectItem value="Mme">{civilityLabel("Mme")}</SelectItem>
                  </SelectContent>
                </Select>
              </FicheField>
              <FicheField label={t("account.firstName")} readValue={collab.firstName} {...fieldProps("firstName")}>
                <Input value={collab.firstName} onChange={(e) => onChange("firstName", e.target.value)} placeholder={t("account.firstNamePlaceholder")} />
              </FicheField>
              <FicheField label={t("account.lastName")} readValue={collab.lastName} {...fieldProps("lastName")}>
                <Input value={collab.lastName} onChange={(e) => onChange("lastName", e.target.value)} placeholder={t("account.lastNamePlaceholder")} />
              </FicheField>
              <div className="sm:col-span-3">
                <FicheField label={t("account.position")} readValue={collab.position} {...fieldProps("position")}>
                  <Input value={collab.position} maxLength={120} onChange={(e) => onChange("position", e.target.value)} placeholder={t("account.positionPlaceholder")} />
                </FicheField>
              </div>
            </div>
          </FicheSection>

          <FicheSection title={t("account.fiche.contact")} icon={<Contact className="h-4 w-4" />} tone="green">
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
              <FicheField label={t("account.emailLabel")} readValue={collab.email} {...fieldProps("email")}>
                <Input type="email" autoComplete="email" value={collab.email} onChange={(e) => onChange("email", e.target.value)} placeholder={t("account.emailPlaceholder")} />
              </FicheField>
              <FicheField label={t("account.phone")} readValue={collab.phoneNumber} {...fieldProps("phoneNumber")}>
                <Input type="tel" autoComplete="tel" value={collab.phoneNumber} onChange={(e) => onChange("phoneNumber", e.target.value)} placeholder={t("account.phonePlaceholder")} />
              </FicheField>
              <div className="sm:col-span-2">
                <FicheField
                  label={t("account.linkedin")}
                  readValue={collab.linkedinUrl}
                  readNode={linkedinHref ? (
                    <a href={linkedinHref} target="_blank" rel="noopener noreferrer" className="text-[#384872] underline decoration-[#A6D3F9] underline-offset-2 hover:decoration-[#384872] break-all" onClick={(e) => e.stopPropagation()}>
                      {collab.linkedinUrl}
                    </a>
                  ) : undefined}
                  {...fieldProps("linkedinUrl")}
                >
                  <Input type="url" value={collab.linkedinUrl || ""} maxLength={300} onChange={(e) => onChange("linkedinUrl", e.target.value)} placeholder="https://linkedin.com/in/..." />
                </FicheField>
              </div>
            </div>
          </FicheSection>

          <FicheSection title={t("account.fiche.languages")} icon={<Languages className="h-4 w-4" />} tone="violet">
            {modeOf("languages") === "read" ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                  {/* Les descriptions restent visibles en lecture : les deux langues se ressemblent
                      et ne commandent pas la même chose. */}
                  <FicheField label={t("account.displayLanguage")} readValue={languageLabel(collab.language)} help={t("account.displayLanguageHelp")} {...fieldProps("languages")} />
                  <FicheField label={t("account.spokenLanguage")} readValue={spokenLabel} help={t("account.spokenLanguageHelp")} {...fieldProps("languages")} />
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{t("account.languagesNote")}</p>
              </div>
            ) : (
              // Édition : le même composant (et la même explication) que dans le wizard de création.
              <FicheField bare {...fieldProps("languages")}>
                <CollaboratorLanguageFields
                  language={collab.language}
                  spokenLanguage={collab.spokenLanguage}
                  onChange={(field, value) => onChange(field, value)}
                  columns={2}
                />
              </FicheField>
            )}
          </FicheSection>

          <FicheSection title={t("account.fiche.role")} icon={<Network className="h-4 w-4" />} tone="yellow">
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
              <FicheField label={t("account.profileLabel")} readValue={profileLabel(collab.profileType)} {...fieldProps("profileType")}>
                <Select value={collab.profileType || "BNF"} onValueChange={(value) => onChange("profileType", value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {profileChoices.map((p) => (
                      <SelectItem key={p} value={p} disabled={!profileOptions.includes(p)}>
                        {profileLabel(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FicheField>

              {/* Rattachement : affiché pour TOUS les profils (un MNG a lui aussi un responsable). */}
              <FicheField
                label={t("account.responsable")}
                readValue={manager ? `${manager.firstName} ${manager.lastName}` : t("account.noResponsable")}
                {...fieldProps("managerId")}
              >
                <Select value={collab.managerId || "none"} onValueChange={(value) => onChange("managerId", value === "none" ? null : value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("account.noResponsable")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("account.noResponsable")}</SelectItem>
                    {managerCandidates.map((mgr) => (
                      <SelectItem key={mgr.id} value={mgr.id}>{mgr.firstName} {mgr.lastName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FicheField>
            </div>
          </FicheSection>

          {/* Simulateurs (licences) — gérables en LECTURE : l'attribution est immédiate. */}
          <FicheSection title={t("account.fiche.simulators")} icon={<KeyRound className="h-4 w-4" />} tone="coral">
            <div className="flex flex-wrap gap-2 items-center" data-testid="fiche-licences">
              {collab.licences.length > 0 ? (
                collab.licences.map((lic) => {
                  const pool = licencePool.find((lp) => lp.type === lic);
                  const label = pool?.label || lic;
                  return (
                    <span
                      key={lic}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-white shadow-sm"
                      style={{ backgroundColor: pool?.color || "#6B7280" }}
                    >
                      {label}
                      {rights.licences && (
                        <button
                          type="button"
                          onClick={() => onToggleLicence(lic)}
                          className="hover:bg-white/25 rounded-full p-0.5 transition-colors"
                          aria-label={t("account.fiche.removeLicence", { licence: label })}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground">{t("account.noLicence")}</span>
              )}
              {rights.licences && addableLicences.length > 0 && (
                // Menu Radix (rendu hors de la fiche) plutôt que l'ancien popover positionné en
                // absolu : il aurait été rogné par le `overflow-hidden` de la fenêtre.
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5 rounded-full border-2 border-dashed border-[#384872]/30 bg-white text-[#384872] hover:border-[#384872]/60"
                    >
                      <Plus className="h-4 w-4" />
                      {t("account.fiche.addLicence")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[240px]">
                    {addableLicences.map((lp) => {
                      const available = Math.max(0, lp.total - lp.used);
                      const exhausted = available === 0;
                      return (
                        <DropdownMenuItem
                          key={lp.type}
                          disabled={exhausted}
                          onSelect={() => { if (!exhausted) onToggleLicence(lp.type); }}
                          className="gap-2"
                        >
                          <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: lp.color }} />
                          <span className="flex-1">{lp.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {exhausted ? t("account.exhausted") : t("account.availableCount", { count: available })}
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {/* Simulateurs choisis pour chaque licence Starter / Focus (choix PERSONNEL). */}
            {licenceSimulators && collab.licences.map((lic) => {
              const info = licenceSimulators(lic);
              if (!info) return null;
              const label = licencePool.find((lp) => lp.type === lic)?.label || lic;
              const chosen = info.ids && info.ids.length > 0;
              return (
                <div key={lic} className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="fiche-licence-simulators">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-[#384872]">{t("account.licenceSimulators.ficheLabel", { licence: label })}</span>
                    {rights.licences && onEditLicenceSimulators && (
                      <Button variant="outline" size="sm" className="h-7" onClick={() => onEditLicenceSimulators(lic)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        {chosen ? t("account.licenceSimulators.edit") : t("account.licenceSimulators.choose")}
                      </Button>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {chosen && info.names ? (
                      info.names.map((name) => (
                        <span key={name} className="inline-flex items-center rounded-full bg-[#988EF2]/20 px-2.5 py-1 text-xs font-medium text-[#5A4BBE]">
                          {name}
                        </span>
                      ))
                    ) : chosen ? (
                      <span className="text-xs text-muted-foreground">{t("account.licenceSimulators.chosenCount", { count: info.ids!.length })}</span>
                    ) : (
                      <span className="text-xs text-[#926B00]">{t("account.licenceSimulators.notChosen", { count: info.max })}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {rights.licences && (
              <p className="mt-3 text-xs text-muted-foreground">{t("account.fiche.licencesHelp")}</p>
            )}
          </FicheSection>
        </div>

        {/* Pied — édition globale uniquement */}
        {editing && (
          <div className="flex justify-end gap-2 px-6 py-3 bg-white border-t border-slate-200 shadow-[0_-2px_6px_-2px_rgba(27,36,64,0.12)]">
            <Button variant="outline" size="sm" className="h-9 border-2 shadow-sm" onClick={onCancelEdit} disabled={saving}>
              <X className="h-4 w-4 mr-2" />
              {t("common.cancel")}
            </Button>
            <Button size="sm" className="h-9 bg-[#1E7A3C] hover:bg-[#186532] text-white shadow-md" onClick={onSave} disabled={saving} data-testid="fiche-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {t("common.save")}
            </Button>
          </div>
        )}

        <ResizeHandles onPointerDown={onPointerDown} />
      </div>

      <ProfilePhotoDialog
        open={photoOpen}
        currentPhoto={collab.profilePicture}
        initials={initials}
        onCancel={() => setPhotoOpen(false)}
        onConfirm={async (photo) => {
          const error = await onSavePhoto(photo);
          if (!error) setPhotoOpen(false);
          return error;
        }}
      />

      {/* Fermer avec des modifications non enregistrées */}
      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("account.fiche.discardTitle")}</DialogTitle>
            <DialogDescription>{t("account.fiche.discardDescription")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDiscard(false)}>
              {t("account.fiche.keepEditing")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDiscard(false);
                onCancelEdit();
                onClose();
              }}
            >
              {t("account.fiche.discardConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Une section = une carte délimitée, avec sa pastille de couleur (relief + repérage). */
function FicheSection({ title, icon, tone, children }: { title: string; icon: React.ReactNode; tone: keyof typeof SECTION_TONES; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(27,36,64,0.08),0_4px_12px_-6px_rgba(27,36,64,0.12)]">
      <header className="flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-100">
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${SECTION_TONES[tone]}`}>{icon}</span>
        <h3 className="font-['Sora'] font-semibold text-sm text-[#384872]">{title}</h3>
      </header>
      <div className="px-4 py-4">{children}</div>
    </section>
  );
}

type FieldMode = "read" | "global" | "single";

interface FicheFieldProps {
  label?: string;
  fieldKey: FicheFieldKey;
  mode: FieldMode;
  canEdit: boolean;
  busy: boolean;
  error: string | null;
  readValue?: string | null;
  /** Rendu de lecture enrichi (ex. lien LinkedIn cliquable) ; sinon `readValue` en texte. */
  readNode?: React.ReactNode;
  /** Explication sous la valeur (lecture). En édition, le contrôle porte la sienne. */
  help?: string;
  /** Le contrôle porte déjà ses libellés (ex. les deux langues) : pas de `Label` en plus. */
  bare?: boolean;
  onStart: () => void;
  onSave: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}

/**
 * Un champ de la fiche, dans l'un de ses trois états. Le contrôle d'édition (`children`) est
 * celui du formulaire d'origine — ce composant ne fait que décider quand le montrer.
 */
function FicheField({ label, fieldKey, mode, canEdit, busy, error, readValue, readNode, help, bare, onStart, onSave, onCancel, children }: FicheFieldProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // À l'ouverture d'un champ seul, le focus va sur son contrôle (clavier : Entrée → saisir).
  useEffect(() => {
    if (mode !== "single") return;
    const target = ref.current?.querySelector<HTMLElement>("input, button[role='combobox']");
    target?.focus();
  }, [mode]);

  if (mode === "global") {
    return (
      <div className="space-y-1.5" data-fiche-field={fieldKey}>
        {!bare && <Label className="text-xs font-medium text-[#384872]/80">{label}</Label>}
        {children}
      </div>
    );
  }

  if (mode === "single") {
    return (
      <div
        ref={ref}
        className="space-y-1.5 rounded-lg bg-[#A6D3F9]/15 ring-1 ring-[#A6D3F9] -m-2 p-2"
        data-fiche-field={fieldKey}
        data-fiche-field-editing="true"
        onKeyDown={(e) => {
          // Seulement pour ce qui vient DU champ : une liste déroulante ouverte (rendue hors
          // de la fiche) gère son propre Échap.
          if (!ref.current?.contains(e.target as Node)) return;
          if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); }
          if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") { e.preventDefault(); onSave(); }
        }}
      >
        {!bare && <Label className="text-xs font-medium text-[#384872]/80">{label}</Label>}
        <div className="flex items-start gap-1">
          <div className="flex-1 min-w-0">{children}</div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 border-2 border-[#7CBF94] bg-white text-[#1E7A3C] hover:bg-[#7CBF94]/15 hover:text-[#1E7A3C] shadow-sm"
            onClick={onSave}
            disabled={busy}
            aria-label={t("account.fiche.saveField")}
            title={t("account.fiche.saveField")}
            data-testid="fiche-field-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 border-2 border-[#F49390] bg-white text-[#C0423F] hover:bg-[#F49390]/15 hover:text-[#C0423F] shadow-sm"
            onClick={onCancel}
            disabled={busy}
            aria-label={t("common.cancel")}
            title={t("common.cancel")}
            data-testid="fiche-field-cancel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {error && <p className="text-xs text-[#C0423F]">{error}</p>}
      </div>
    );
  }

  return (
    <div
      className={`group space-y-1 rounded-lg -m-2 p-2 outline-none transition-colors ${
        canEdit ? "cursor-text hover:bg-[#A6D3F9]/15 focus-visible:ring-2 focus-visible:ring-[#384872]/30" : ""
      }`}
      data-fiche-field={fieldKey}
      tabIndex={canEdit ? 0 : undefined}
      title={canEdit ? t("account.fiche.doubleClickHint") : undefined}
      onDoubleClick={canEdit ? () => { window.getSelection()?.removeAllRanges(); onStart(); } : undefined}
      onKeyDown={canEdit ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) { e.preventDefault(); onStart(); } } : undefined}
    >
      <div className="flex items-center justify-between gap-2 min-h-5">
        <span className="text-xs font-medium text-[#384872]/65">{label}</span>
        {canEdit && (
          // Le double-clic n'existe pas au doigt et ne se devine pas : un petit crayon, visible
          // au survol / au focus (et toujours sur mobile), ouvre le même champ.
          <button
            type="button"
            tabIndex={-1}
            onClick={onStart}
            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 text-[#384872]/50 hover:text-[#384872] transition-opacity"
            aria-label={t("account.fiche.editField", { field: label })}
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="text-sm font-medium text-[#384872] break-words min-h-5">
        {readNode ?? (readValue ? readValue : <span className="font-normal text-muted-foreground">{t("common.none")}</span>)}
      </div>
      {help && <p className="text-xs text-muted-foreground leading-relaxed">{help}</p>}
    </div>
  );
}
